import { useState, useEffect, createContext, useContext, useCallback, type ReactNode } from "react";

interface RouterContext {
	path: string;
	params: Record<string, string>;
	navigate: (to: string) => void;
}

const RouterCtx = createContext<RouterContext>({
	path: "/",
	params: {},
	navigate: () => {},
});

export function useRouter() {
	return useContext(RouterCtx);
}

export function Router({ children }: { children: ReactNode }) {
	const [path, setPath] = useState(window.location.pathname);

	const navigate = useCallback((to: string) => {
		window.history.pushState({}, "", to);
		setPath(to);
	}, []);

	useEffect(() => {
		const onPop = () => setPath(window.location.pathname);
		window.addEventListener("popstate", onPop);
		return () => window.removeEventListener("popstate", onPop);
	}, []);

	return (
		<RouterCtx.Provider value={{ path, params: {}, navigate }}>
			{children}
		</RouterCtx.Provider>
	);
}

interface RouteProps {
	path: string;
	component: React.ComponentType<{ params: Record<string, string> }>;
}

export function Routes({ routes }: { routes: RouteProps[] }) {
	const { path } = useRouter();

	for (const route of routes) {
		const params = matchRoute(route.path, path);
		if (params !== null) {
			const Component = route.component;
			return <Component params={params} />;
		}
	}

	return null;
}

function matchRoute(pattern: string, path: string): Record<string, string> | null {
	const patternParts = pattern.split("/").filter(Boolean);
	const pathParts = path.split("/").filter(Boolean);

	if (patternParts.length !== pathParts.length) return null;

	const params: Record<string, string> = {};
	for (let i = 0; i < patternParts.length; i++) {
		if (patternParts[i].startsWith(":")) {
			params[patternParts[i].slice(1)] = pathParts[i];
		} else if (patternParts[i] !== pathParts[i]) {
			return null;
		}
	}
	return params;
}

export function Link({
	to,
	children,
	className,
}: {
	to: string;
	children: ReactNode;
	className?: string;
}) {
	const { navigate } = useRouter();
	return (
		<a
			href={to}
			className={className}
			onClick={(e) => {
				e.preventDefault();
				navigate(to);
			}}
		>
			{children}
		</a>
	);
}
